import { getTenantFrontendConfig, loginTenant } from '@/api/tenant';
import { login as loginLegacy } from '@/api/auth';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { customerServiceUrl } from '@/lib/customerService';

export default function Login() {
  const navigate = useNavigate();
  const [account, setAccount] = useState('');
  const [password, setPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [lockCountdown, setLockCountdown] = useState(0);
  const [brandName, setBrandName] = useState('AI 投资助手');

  useEffect(() => {
    getTenantFrontendConfig().then((data) => setBrandName(data.brandName)).catch(() => {});
  }, []);

  const handleLogin = async () => {
    setError('');
    if (!/^[A-Za-z0-9_]{3,64}$/.test(account.trim())) { setError('请输入有效账号'); return; }
    if (!password) { setError('请输入密码'); return; }
    setLoading(true);
    try {
      await loginTenant(account.trim(), password);
      navigate('/');
    } catch (e: any) {
      let loginError = e;
      const isCompatibilityHost =
        ['piaosuoai.com', 'www.piaosuoai.com', 'tiandingyunoffice.com', 'ai.tiandingyunoffice.com'].includes(window.location.hostname) ||
        // 18107 is the isolated selective-feature preview and must exercise the
        // same legacy-account fallback as the three production hosts.
        ['18082', '18085', '18086', '18107'].includes(window.location.port);
      const isCrossTenantLegacy =
        e?.code === 'LEGACY_UID_ALREADY_BOUND' ||
        e?.code === 'LEGACY_ASSIGNED_TO_OTHER_TENANT';

      if (isCompatibilityHost && isCrossTenantLegacy) {
        try {
          localStorage.removeItem('tenant_gateway_token');
          localStorage.removeItem('tenant_key');
          localStorage.removeItem('beta_uid');
          await loginLegacy(account.trim(), password);
          navigate('/');
          return;
        } catch (legacyError: any) {
          loginError = legacyError;
        }
      }

      const msg = loginError?.message || '登录失败';
      const remaining = loginError?.remainingAttempts;
      const lockSec = loginError?.lockSeconds;
      if (lockSec) {
        setError('尝试次数过多，请 ' + Math.ceil(lockSec / 60) + ' 分钟后重试');
        setLockCountdown(lockSec);
      } else if (remaining && remaining <= 3 && remaining > 0) {
        setError(msg + '。还剩 ' + remaining + ' 次机会，忘记密码可以重置');
      } else {
        setError(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  // 倒计时
  useEffect(() => {
    if (lockCountdown <= 0) return;
    const t = setInterval(() => {
      setLockCountdown((c: number) => {
        if (c <= 1) { clearInterval(t); return 0; }
        return c - 1;
      });
    }, 1000);
    return () => clearInterval(t);
  }, [lockCountdown]);

  return (
    <>
      {/* Logo */}
      <div className="text-center mb-10">
        <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center mx-auto mb-4">
          <i className="ri-robot-2-fill text-white text-3xl" />
        </div>
        <h1 className="text-2xl font-bold text-foreground-950">{brandName}</h1>
        <p className="text-sm text-foreground-500 mt-1">AI 投资助手</p>
        <div className="flex items-center justify-center gap-1.5 mt-3">
          <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
          <span className="text-xs text-foreground-500">系统运行正常 · A股行情接入中</span>
        </div>
      </div>

      {/* Form */}
      <div className="space-y-4">
        <div>
          <label className="text-xs font-medium text-foreground-600 mb-1.5 block">账号</label>
          <div className="relative">
            <i className="ri-user-3-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-300 text-sm" />
            <input type="text" value={account} onChange={(e) => setAccount(e.target.value)} maxLength={64}
              placeholder="请输入账号"
              className="w-full bg-white border border-background-200 rounded-xl pl-10 pr-4 py-3 text-sm text-foreground-950 placeholder:text-foreground-300 outline-none focus:border-primary-400 transition-colors" />
          </div>
        </div>
        <div>
          <label className="text-xs font-medium text-foreground-600 mb-1.5 block">密码</label>
          <div className="relative">
            <i className="ri-lock-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-300 text-sm" />
            <input type={showPwd ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleLogin(); }} placeholder="请输入密码"
              className="w-full bg-white border border-background-200 rounded-xl pl-10 pr-10 py-3 text-sm text-foreground-950 placeholder:text-foreground-300 outline-none focus:border-primary-400 transition-colors" />
            <button onClick={() => setShowPwd(!showPwd)} className="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer">
              <i className={`${showPwd ? 'ri-eye-line' : 'ri-eye-off-line'} text-foreground-300 text-sm`} />
            </button>
          </div>
        </div>

        {error && <p className="text-xs text-red-500">{error}{lockCountdown > 0 ? " (" + Math.floor(lockCountdown / 60) + "分" + (lockCountdown % 60) + "秒)" : ""}</p>}

        <div className="flex justify-end">
          <a href="/forgot-password" className="text-xs text-primary-500 cursor-pointer">忘记密码？</a>
        </div>

        <button onClick={handleLogin} disabled={loading}
          className="w-full py-3 bg-gradient-to-r from-primary-500 to-accent-500 text-white rounded-xl text-sm font-semibold cursor-pointer whitespace-nowrap disabled:opacity-60">
          {loading ? '登录中...' : '登录'}
        </button>
      </div>

      <p className="text-center mt-6 text-sm text-foreground-500">
        没有内测账号？
        <a href={customerServiceUrl('login')} className="text-primary-500 font-medium cursor-pointer">
          添加客服领取内测账号
        </a>
      </p>

      <p className="text-[10px] text-foreground-400 text-center mt-6">
        本产品为学习与模拟演示工具，不构成投资建议
      </p>
    </>
  );
}
